import { ChangeEventHandler } from "react"
import EagerSVG from "./EagerSVG"
import { PhoneInput } from "react-international-phone"
import 'react-international-phone/style.css';

// ========== TextInput Component ==========
type InputType = "text" | "email" | "password" | "number" | "search" | "date"
type InputSize = "tiny" | "small" | "default" | "medium" | "large"
type IconType = "user" | "email" | "password" | "search" | "website"

interface TextInputProps {
    label: string
    type: InputType
    placeholder?: string
    name?: string
    value: string | number
    required?: boolean
    size?: InputSize
    icon?: IconType
    customClass?: string
    maxLength?: number
    description?: string
    disabled?: boolean
    onChange: ChangeEventHandler<HTMLInputElement>
    onBlur?: ChangeEventHandler<HTMLInputElement>
    fitWidth?: boolean
    autoComplete?: "on" | "off"
}

export const TextInput = ({
    label,
    type = "text",
    placeholder,
    value,
    name,
    required,
    size = "default",
    icon,
    customClass,
    onChange,
    onBlur,
    maxLength,
    description,
    disabled,
    fitWidth = false,
    autoComplete = "off"
}: TextInputProps) => {
    // Map icon type to React Icons
    const getIcon = () => {
        switch (icon) {
            case "search":
                return <EagerSVG name="SearchIcon" width={20} height={20} />
            default:
                return null
        }
    };

    return (
        <div className={`relative ${fitWidth ? "" : "w-full"}`}>
            {label && (
                <label className="text-[14px] -mb-3.5 dark:text-white text-left" htmlFor={label.toLowerCase()}>
                    {label}:
                </label>
            )}
            <div className="relative flex items-center">
                {icon && icon !== "password" && (
                    <span className="absolute left-3">{getIcon()}</span>
                )}
                <input
                    className={`border dark:border-darkMode-layer3border dark:focus:border-white rounded-sm ${disabled ? "dark:bg-darkMode-formBgDisabled dark:text-darkMode-mutedText" : "dark:bg-darkMode-formBg dark:text-white"} transition-all duration-300 dark:placeholder:text-darkMode-mutedText 
            ${size === "large" ? "px-5 py-4" : size === "tiny" ? "text-[14px] px-3 py-1" : size === "small" ? "px-3.5 py-2.5" : "px-4 py-3.5"} ${icon ? (icon === "password" ? "pr-10" : "pl-10") : ""} ${fitWidth ? "" : "w-full"} ${customClass && customClass}`}
                    type={type}
                    name={name ? name : label.toLowerCase()}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    required={required}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    onChange={onChange}
                    onBlur={onBlur}
                />
                {icon === "password" && (
                    <span className="absolute right-3">{getIcon()}</span>
                )}
            </div>
            {description && description.length > 0 && <p className="dark:text-darkMode-mutedText text-[12px]">{description}</p>}
        </div>
    )
}

type PhoneNumberProps = {
    label: string
    value: string
    required?: boolean
    onBlur?: () => void
    setValue: (value: string) => void
}
export const PhoneNumber = ({ label, value, setValue, required = false, onBlur }: PhoneNumberProps) => {
    return (
        <div className="relative">
            <label
                className="text-[14px] -mb-3.5 dark:text-white text-left"
                htmlFor={label.toLowerCase()}
            >
                {label}:
            </label>

            <PhoneInput
                defaultCountry="nl"
                value={value}
                required={required}
                onChange={setValue}
                name={label.toLowerCase()}
                onBlur={onBlur}
                // Input classes
                inputClassName="!text-[16px]
        border dark:!border-darkMode-layer3border
        dark:!focus:border-white
        !rounded-br-sm !rounded-tr-sm dark:!bg-darkMode-formBg
        dark:!text-white
        dark:placeholder:!text-darkMode-mutedText
        !px-3.5 !py-7 !transition-all !duration-300
        !w-full         
          "
                // Wrapper classes
                className="w-full flex items-center"

                // Country flag dropdown classes
                countrySelectorStyleProps={{
                    buttonClassName:
                        '!border-r dark:!border-darkMode-layer3border !px-4 !py-7 dark:!bg-darkMode-formBg ',
                    dropdownStyleProps: {
                        className: 'dark:!bg-darkMode-layer1color dark:!text-white dark:!border-darkMode-layer3border !border !border-solid !border-darkMode-layer3border !important !focus:!border-darkMode-layer3border !focus-visible:!border-darkMode-layer3border !active:!border-darkMode-layer3border !shadow-none',
                        listItemClassName: 'dark:hover:!bg-darkMode-layer2color dark:focus:!bg-darkMode-layer2color !p-2 !bg-inherit !text-inherit !focus:!bg-darkMode-layer2color !focus-visible:!bg-darkMode-layer2color !active:!bg-darkMode-layer2color',
                    }
                }}
            />
        </div>
    );
};


// ========== Checkbox Component ==========
type CheckboxSize = "small" | "default" | "large"
type CheckboxProps = {
    checked: boolean
    onChange: (checked: boolean) => void
    label?: string
    id?: string
    name?: string
    disabled?: boolean
    size?: CheckboxSize
    customClass?: string
}

export const Checkbox: React.FC<CheckboxProps> = ({
    checked,
    onChange,
    label,
    id,
    name,
    disabled = false,
    size = "default",
    customClass,
}) => {
    return (
        <label htmlFor={id} className="flex items-center gap-2 select-none cursor-pointer align-middle w-fit">
            <input
                id={id}
                name={name}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                className={`peer hidden`}
            />
            <div
                className={`shrink-0 box-border transition-colors duration-200 ${size === "small" ? "w-4 h-4" : "w-6 h-6"} flex items-center justify-center rounded border dark:border-darkMode-primary peer-checked:dark:bg-darkMode-primary ${customClass}`}>
                {checked === true && (
                    <svg
                        className={`${size === "small" ? "w-3 h-3" : "w-5 h-5"} text-black`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                )}

            </div>
            {label && <span className={`${size === "large" ? "text-[16px]" : "text-sm"}`}>{label}</span>}
        </label>
    )
}


// ======= Select Dropdown Component =======
type Option<T> = {
    value: T
    label: string
}
type SelectSize = "tiny" | "small" | "default" | "medium" | "large"
type SelectDropdownProps<T> = {
    options: Option<T>[]
    value?: T
    placeholder?: string
    onChange: (value: T) => void
    useIndexAsValue?: boolean
    size?: SelectSize
    customClasses?: string
}

export function SelectDropdown<T>({
    options,
    value,
    placeholder,
    onChange,
    useIndexAsValue = false,
    customClasses = "",
    size = "default",
}: SelectDropdownProps<T>) {
    const isUnselected = value === undefined || value === null || value === ""

    const selectedValue = isUnselected
        ? ""
        : useIndexAsValue
            ? options.findIndex(opt => opt.value === value)
            : String(value)

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedRaw = e.target.value

        if (useIndexAsValue) {
            const index = Number(selectedRaw)
            if (!isNaN(index) && index >= 0 && index < options.length) {
                onChange(options[index].value)
            }
        } else {
            const selectedOption = options.find(
                opt => String(opt.value) === selectedRaw
            );
            if (selectedOption) {
                onChange(selectedOption.value)
            }
        }
    }

    return (
        <select
            className={`border dark:border-darkMode-layer3border rounded-sm dark:bg-darkMode-formBg dark:text-darkMode-secondaryText
            ${size === "large"
                    ? "px-5 py-4 text-lg"
                    : size === "medium"
                        ? "px-4 py-3 text-base"
                        : size === "small"
                            ? "px-3.5 py-[11.5px] text-sm"
                            : size === "tiny"
                                ? "px-2 py-1 text-xs"
                                : "px-4 py-4"
                }
            ${customClasses}`}
            value={selectedValue}
            onChange={handleChange}
        >
            {placeholder && (
                <option value="" disabled>
                    {placeholder}
                </option>
            )}
            {options.map((option, index) => (
                <option
                    key={index}
                    value={useIndexAsValue ? index : String(option.value)}
                >
                    {option.label}
                </option>
            ))}
        </select>
    )
}


// ========== Toggle Checkbox Component ==========
type ToggleCheckboxProps = {
    checked: boolean
    onChange: (checked: boolean) => void
    label?: string
    id?: string
    name?: string
    disabled?: boolean
    customClass?: string
}
export const ToggleCheckbox: React.FC<ToggleCheckboxProps> = ({
    checked,
    onChange,
    label,
    id,
    name,
    disabled = false,
    customClass,
}) => {
    return (
        <div className="flex gap-x-3 items-center">
            <label
                htmlFor={id}
                className={`relative inline-flex items-center ${disabled ? "" : "cursor-pointer"
                    }`}
            >
                <input
                    id={id}
                    name={name}
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <div
                    className={`w-11 h-6 dark:bg-darkMode-toggleCheckboxBg rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:dark:bg-white after:dark:border-darkMode-muted after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:dark:bg-darkMode-primary ${customClass}`}
                ></div>
            </label>
            {(label !== undefined && label !== "") && (
                <label htmlFor={id}>{label}</label>
            )}
        </div>
    );
};

